import { useState, useEffect } from 'react';
import {
  media,
  MediaEventType,
  type CameraId,
  type MicrophoneId,
  type SpeakerId,
} from '../../webrtc/media';
import { session } from '../../webrtc/session';
import { useAppSelector } from '../useReduxToolkit';
import { getSessionDetails } from '../../features/session/session.selectors';

const changeCamera = async (
  deviceId: string,
): ReturnType<typeof media.changeCamera> =>
  media.changeCamera(deviceId as CameraId);

const changeMicrophone = async (
  deviceId: string,
): ReturnType<typeof media.changeMicrophone> =>
  media.changeMicrophone(deviceId as MicrophoneId);

type UseDevicesReturnType = {
  cameras: MediaDeviceInfo[];
  microphones: MediaDeviceInfo[];
  speakers: MediaDeviceInfo[];
  selectedCameraId: CameraId | undefined;
  selectedMicrophoneId: MicrophoneId | undefined;
  selectedSpeakerId: SpeakerId | undefined;
  changeCamera: (deviceId: string) => Promise<void>;
  changeMicrophone: (deviceId: string) => Promise<void>;
  changeSpeaker: (deviceId: string) => Promise<void>;
};

export function useDevices(): UseDevicesReturnType {
  const [cameras, setCameras] = useState<MediaDeviceInfo[]>(media.cameras);
  const [microphones, setMicrophones] = useState<MediaDeviceInfo[]>(
    media.microphones,
  );
  const [speakers, setSpeakers] = useState<MediaDeviceInfo[]>(media.speakers);

  const [selectedCameraId, setSelectedCameraId] = useState<
    CameraId | undefined
  >(media.selectedCameraId);
  const [selectedMicrophoneId, setSelectedMicrophoneId] = useState<
    MicrophoneId | undefined
  >(media.selectedMicrophoneId);
  const [selectedSpeakerId, setSelectedSpeakerId] = useState<
    SpeakerId | undefined
  >(media.selectedSpeakerId);

  const { isConnected } = useAppSelector(getSessionDetails);

  const changeSpeaker = async (
    deviceId: string,
  ): ReturnType<typeof media.changeSpeaker> => {
    if (isConnected) {
      await session.changeRemoteAudioOutputDevice(deviceId);
    }
    await media.changeSpeaker(deviceId as SpeakerId);
  };

  const setDevices = (): void => {
    setCameras(media.cameras);
    setMicrophones(media.microphones);
    setSpeakers(media.speakers);
  };

  const setSelectedDevices = (): void => {
    setSelectedCameraId(media.selectedCameraId);
    setSelectedMicrophoneId(media.selectedMicrophoneId);
    setSelectedSpeakerId(media.selectedSpeakerId);
  };

  useEffect(() => {
    const unsubscribes = [
      media.on([MediaEventType.DevicesChange], () => setDevices()),
      media.on([MediaEventType.SelectedDevicesChange], () =>
        setSelectedDevices(),
      ),
    ];

    return () => {
      for (const unsubscribe of unsubscribes) {
        unsubscribe();
      }
    };
  }, []);

  return {
    cameras,
    microphones,
    speakers,
    selectedCameraId,
    selectedMicrophoneId,
    selectedSpeakerId,
    changeCamera,
    changeMicrophone,
    changeSpeaker,
  };
}
